import { BaseClass } from '@components/BaseClass.js';
import { catchError, combineLatest, filter, map, Observable, of, take, tap } from 'rxjs';
import { debounceTime, shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { LoadingThrobber } from '@components/Utility/LoadingThrobber/LoadingThrobber.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { extractTemplateTokens, forEachKey, getID, isCtrlKey, replaceTokens } from '@components/Utility/index.js';
import { eventContextGet } from '@components/EventConfigManager/EventConfigManagerUtil.js';
import { ENV } from '@components/Environment.js';
/**
 * Represents a component in the system.
 */
export class Component extends BaseClass {
    get progress() { return this._progress; }
    get loadingThrobberRootElement() { return this.loadingThrobberRoot || this.rootElement; }
    get helpAnchor() { return this._helpAnchor; }
    set helpAnchor(h) {
        this._propertySet('helpAnchor', h);
        if (this.drawn) {
            this.renderHelpAnchor();
        }
    }
    get seq() { return this._seq; }
    set seq(value) { this._propertySet('seq', value); }
    get drawn() { return this._drawn; }
    set drawn(value) {
        this._propertySet('drawn', value);
    }
    get drawn$() {
        return this.propertyGet$('drawn')
            .pipe(filter(b => b), debounceTime(0) // component.ts sets this before some components are done their draw method
        , take(1));
    }
    get disabledState() { return !!this._disabledState; }
    get disabled() { return this._disabled; }
    set disabled(value) {
        if (this._disabled !== value) {
            this._propertySet('disabled', value);
            this.updateDisabledState();
        }
    }
    get hiddenProperty() { return this._hiddenProperty; }
    set hiddenProperty(value) {
        this._propertySet('hiddenProperty', value);
    }
    get hiddenState() { return !!this._hiddenState; }
    get hidden() { return this._hidden; }
    set hidden(value) {
        if (this._hidden !== value) {
            this._propertySet('hidden', value);
            this.updateHiddenState();
        }
    }
    get autoResize() {
        return this._fixedHeight ? false : !!this._autoResize;
    }
    set autoResize(val) {
        this._propertySet('autoResize', val);
        if (this.drawn) {
            this.renderAutoResizeClass();
        }
    }
    get fixedHeight() { return this._fixedHeight; }
    set fixedHeight(value) {
        this._propertySet('fixedHeight', value);
        if (value) {
            this.renderAutoResizeClass();
            this.height = value;
        }
        else if (this._autoResize) {
            this.renderAutoResizeClass();
        }
        if (this.drawn) {
            this.formContext?.handleResize();
            // DIContainer.getInstance<ComponentRegistry>('ComponentRegistry').getFormComponentByElement(this.rootElement)?.handleResize();
        }
    }
    get height() { return this._fixedHeight || this._height; }
    set height(value) {
        if (this.fixedHeight) {
            value = this._fixedHeight;
        }
        this._propertySet('height', value);
        if (this.drawn) {
            this.renderHeight();
        }
    }
    get width() { return this._width; }
    set width(value) {
        this._propertySet('width', value);
        if (this.drawn) {
            this.renderWidth();
        }
    }
    get flexGrow() { return this._flexGrow; }
    set flexGrow(value) {
        this._propertySet('flexGrow', value);
        if (this.drawn) {
            this.renderFlexGrow();
        }
    }
    get flexShrink() { return this._flexShrink; }
    set flexShrink(value) {
        this._propertySet('flexShrink', value);
        if (this.drawn) {
            this.renderFlexShrink();
        }
    }
    get flexBasis() { return this._flexBasis; }
    set flexBasis(value) {
        this._propertySet('flexBasis', value);
        if (this.drawn) {
            this.renderFlexBasis();
        }
    }
    get record() { return this._record; }
    set record(record) {
        this._propertySet('record', record);
    }
    get recordState() { return this._recordState; }
    set recordState(value) {
        if (this._recordState !== value) {
            this._propertySet('recordState', value);
            if (!this.destroyed) {
                this.handleRecordStateChange(value);
            }
        }
    }
    get enabledRecordStates() { return this._enabledRecordStates; }
    set enabledRecordStates(states) {
        this._propertySet('enabledRecordStates', states);
        this.updateDisabledState();
    }
    get visibleRecordStates() { return this._visibleRecordStates; }
    set visibleRecordStates(states) {
        this._propertySet('visibleRecordStates', states);
        this.updateHiddenState();
    }
    get captionKey() { return this._captionKey; }
    set captionKey(value) {
        if (this instanceof DIContainer.getClassConstructor('Panel')) {
            console.log('Panel caption key', value);
        }
        this._propertySet('captionKey', value);
        if (value) {
            this.captionsLoad();
        }
    }
    get captionKeyTokens() { return this._captionKeyTokens; }
    set captionKeyTokens(value) {
        this._propertySet('captionKeyTokens', value);
        if (this.captionKey && value) {
            this.captionsLoad();
        }
    }
    get CaptionManager() {
        if (!this.dependencies.CaptionManager) {
            this.dependencies.CaptionManager = DIContainer.getInstance('CaptionManager');
        }
        return this.dependencies.CaptionManager;
    }
    get classList() { return this._classList; }
    set classList(value) {
        const old = this._classList;
        this._propertySet('classList', value || []);
        if (this.drawn && this.rootElement) {
            old.forEach(cls => this.rootElement.classList.remove(cls));
            this._classList.forEach(cls => this.rootElement.classList.add(cls));
        }
    }
    classListUpdate(className, add) {
        if (add) {
            if (!this._classList.includes(className)) {
                this._classList.push(className);
                if (this.drawn && this.rootElement) {
                    this.rootElement.classList.add(className);
                }
            }
        }
        else {
            const index = this._classList.indexOf(className);
            if (index !== -1) {
                this._classList.splice(index, 1);
                if (this.drawn && this.rootElement) {
                    this.rootElement.classList.remove(className);
                }
            }
        }
    }
    /**
     * Create a new component.
     * @param id - The id of the component.
     * @param rootElement - The root HTML element of the component.
     * @param dependencies
     */
    constructor(id, rootElement, dependencies = {}) {
        super(id, dependencies);
        /** The name of the component. */
        this.componentName = 'Component';
        /** The pk value for this component when used in a database driven form. */
        this.q360FormComponentId = '';
        /** The id of the parent container */
        this.containerId = '';
        this.globalContainerId = '';
        this.focused = false;
        /** The type of HTML element for the components root element. */
        this.rootElementType = 'span';
        this.toolTipCallbackIds = new Map();
        /**
         * list of component events (ie. click) that support being configured in the form designer.
         * the value assigned to each key represents the event category ie. "click" can access events that are supported by "click" or "*"
         * @protected
         */
        this.dynamicCodeEvents = {};
        this._seq = 0;
        /** Indicates if the component is rendered in the DOM. */
        this._drawn = false;
        /** `disabled` property is intended for controlling component disabled state from form level code. */
        this._disabled = false;
        /**
         * Determines whether hiding the component sets the CSS Visibility or Display property
         * Visibility will hide the component but maintain the space that the component consumes
         * Display will hide the component and collpase the space that the component consumes
         * @protected
         */
        this._hiddenProperty = 'display';
        this._hidden = false;
        this.minAutoResize = 300;
        /** Handlers that are executed before the event. */
        this.beforeHandlers = {};
        /** Handlers that are executed when the event happens. */
        this.eventHandlers = {};
        /** Handlers that are executed after the event. */
        this.afterHandlers = {};
        /**
         * handlers that are configured in the form designer with no code
         * @private
         */
        this.dynamicHandlers = {};
        /*
        {
            click: {
                action: 'refreshComponent'
                ,params: ['mytasksgrid']
            }
        }
        * */
        /** a memory-leak-proof way to bind event listeners to a components elements. Listeners will be removed when the component is destroyed.*/
        this.registeredEvents = new Map();
        /** used to help form associate records and components when initializing */
        this.recordName = '';
        this._recordState = 'none';
        /*protected _visibleViews?: ('tech'|'full')[];
        get visibleViews() {return this._visibleViews;}
        set visibleViews(views) {
            this._propertySet('visibleViews', views);
            this.updateHiddenState();
        }*/
        /** captionkey is used to find the caption(s) for this component to support multi language*/
        this._captionKey = '';
        this._captionKeyTokens = {};
        /** The type of drag behavior for the designer. */
        this.designerDragType = 'inline';
        this._classList = [];
        this.hotkeys = {};
        DIContainer.getInstance('ComponentRegistry').registerComponent(this.globalId, this);
        if (rootElement) {
            this.rootElement = rootElement;
        }
        this.captionTypesSet();
        this.dynamicCodeEvents.afterDraw = 'event';
    }
    init() {
        super.init();
        this.globalId = getID(this.id + '_');
        this.formDesignerMode = this.dependencies.formDesignerMode;
        this.recordStore = this.dependencies.recordStore;
        this.formContext = this.dependencies.formContext;
    }
    captionTypesSet() {
    }
    /**
     * Draws the component.
     */
    draw() {
        if (this.loadingThrobber) {
            // sometimes interferes with container components templating. 
            this.loadingThrobber.rootElement.remove();
        }
        this.rootElement.classList.add(`s360${this.componentName}`);
        if (this.formDesignerMode) {
            this.rootElement.classList.add('form-designer');
        }
        if (this.classList.length) {
            this.rootElement.classList.add(...this.classList);
        }
        if (this._hiddenState) {
            this.renderHiddenState();
        }
        this.applyComponentTemplate();
        this.rootElement.setAttribute('data-global-id', this.globalId);
        if (this._disabledState) {
            this.renderDisabledState(true);
        }
        if (this.height) {
            this.renderHeight();
        }
        if (this.width) {
            this.renderWidth();
        }
        if (this.flexBasis) {
            this.renderFlexBasis();
        }
        if (typeof this.flexGrow !== 'undefined') {
            this.renderFlexGrow();
        }
        if (typeof this.flexShrink !== 'undefined') {
            this.renderFlexShrink();
        }
        this.drawn = true;
        if (typeof this._disabledState === 'undefined') {
            this.updateDisabledState();
        }
        if (typeof this._hiddenState === 'undefined') {
            this.updateHiddenState();
        }
        if (this.loadingThrobber) {
            this.appendLoadingThrobber();
        }
        if (this.autoResize) {
            this.renderAutoResizeClass();
        }
        if (this.dynamicHandlers['afterDraw'] || this.eventHandlers['afterDraw']) {
            this.trigger('afterDraw');
        }
        if (this.helpAnchor) {
            this.renderHelpAnchor();
        }
    }
    renderHelpAnchor() {
        // must be defined by component. Though we should try to only support components that use buttonsmixin if possible.
    }
    renderAutoResizeClass() {
        if (!this.rootElement) {
            return;
        }
        if (this.autoResize) {
            this.rootElement.classList.add('autoResize');
        }
        else {
            this.rootElement.classList.remove('autoResize');
        }
    }
    applyComponentTemplate() {
        const template = this.template();
        if (template) {
            this.rootElement.innerHTML = template;
        }
    }
    show() {
        this.hidden = false;
    }
    hide() {
        this.hidden = true;
    }
    /**
     * Handles the change in record state.
     *
     * Handler to be extended for components like actionbar to toggle the state of some buttons based on record state.
     *
     * @param {RecordState} state - The new state of the record.
     */
    handleRecordStateChange(_state) {
        if (this._enabledRecordStates) {
            this.updateDisabledState();
        }
        if (this._visibleRecordStates) {
            this.updateHiddenState();
        }
    }
    /**
     * Updates the disabled state of the component.
     *
     * @protected
     * @return {void}
     */
    updateDisabledState() {
        const shouldDisable = this.shouldDisable();
        if (shouldDisable !== this._disabledState) {
            this._disabledState = shouldDisable;
            if (this.drawn) {
                this.renderDisabledState(shouldDisable);
            }
        }
    }
    /**
     * Updates the hidden state of the component.
     *
     * @protected
     * @return {void}
     */
    updateHiddenState() {
        const shouldHide = this.shouldHide();
        if (shouldHide !== this._hiddenState) {
            this._hiddenState = shouldHide;
            if (this.drawn) {
                this.renderHiddenState();
            }
        }
    }
    /**
     * Determines if the component should be disabled.
     *
     * @protected
     * @return {boolean} - Returns true if the component should be disabled, otherwise returns false.
     */
    shouldDisable() {
        return this._disabled || !!(this._enabledRecordStates && this._enabledRecordStates.length && this._enabledRecordStates.indexOf(this._recordState) === -1);
    }
    /**
     * Determines if the component should be hidden.
     *
     * @protected
     * @return {boolean} - Returns true if the component should be hidden, otherwise returns false.
     */
    shouldHide() {
        // do we need to check the record viewlevel? No, because those components dont even load into the form. 
        return this._hidden
            || !!(this._visibleRecordStates && this._visibleRecordStates.length && this._visibleRecordStates.indexOf(this._recordState) === -1);
        //|| !!(this._visibleViews && this._visibleViews.length && this._visibleViews.indexOf(ENV('viewMode')) === -1);
    }
    /**
     * Applies the disabled state to the component DOM element.
     *
     * @param {boolean} isDisabled - Indicates if the component should be disabled or not.
     */
    renderDisabledState(isDisabled) {
        // Custom logic for disabling the component, should be overridden in subclasses
        if (isDisabled) {
            this.rootElement.classList.add('disabled');
        }
        else {
            this.rootElement.classList.remove('disabled');
        }
    }
    /**
     * Applies the hidden state to the component DOM element.
     */
    renderHiddenState() {
        if (this.rootElement) {
            const isDisplayProperty = this.hiddenProperty === 'display';
            let hiddenClass = isDisplayProperty ? 'd-none' : 'v-hidden';
            if (this.formDesignerMode) {
                hiddenClass = `form-designer-${hiddenClass}`;
            }
            if (this._hiddenState !== this.rootElement.classList.contains(hiddenClass)) {
                this.rootElement.classList.toggle(hiddenClass, this._hiddenState);
                if (!this._hiddenState && this._autoResize) {
                    this.formContext?.debouncedHandleResize();
                }
            }
        }
    }
    captionsLoad() {
        if (this.captionTypes) {
            const setCaptions = () => {
                for (const key in this.captionTypes) {
                    if (captionManager.hasKey(`${this.captionKey}_${key}`)) {
                        const maybeTemplate = this.CaptionManager.captions[`${this.captionKey}_${key}`] || '';
                        this.parseCaption(this.captionTypes[key], maybeTemplate);
                        // this.propertySet(
                        // 	this.captionTypes[key]
                        // 	,replaceTokens(maybeTemplate, this.captionKeyTokens, {
                        // 		records: this.recordStore?.recordsGet()
                        // 	})
                        // );
                        if (!this.disableRecordCaptionKeyTokens) {
                            this.subscribeToRecordCaptionKeyTokens(this.captionTypes[key], maybeTemplate);
                        }
                    }
                    else {
                        this.propertySet(this.captionTypes[key], '');
                    }
                }
            };
            const captionManager = this.CaptionManager;
            if (!this.CaptionManager.hasKey(this.captionKey) && ENV('autoLoadCaptions')) {
                this.CaptionManager.onLoad$(this.captionKey, true)
                    .subscribe(() => {
                    setCaptions();
                });
            }
            else {
                setCaptions();
            }
        }
    }
    parseCaption(property, maybeTemplate) {
        this.propertySet(property, replaceTokens(maybeTemplate, {
            ...this.record?.data || {},
            ...this.captionKeyTokens
        }, {
            records: this.recordStore?.recordsGet()
        }));
    }
    subscribeToRecordCaptionKeyTokens(property, maybeTemplate) {
        if (maybeTemplate && maybeTemplate.indexOf('{{') > -1) {
            const observables = new Map();
            for (const { name, modifier } of extractTemplateTokens(maybeTemplate)) {
                if (modifier === 'caption' || modifier === 'tokenLiteral')
                    continue;
                let token = name;
                let dotIndex = token.indexOf('.');
                if (token && dotIndex === -1 && !Reflect.has(this.captionKeyTokens, token) && this.record) {
                    token = `${this.record.id}.${token}`;
                    dotIndex = token.indexOf('.');
                }
                if (dotIndex > 0 && !observables.has(token)) {
                    const recordName = token.substring(0, dotIndex);
                    const fieldName = token.substring(dotIndex + 1);
                    const record = recordName === this.record?.id ? this.record : this.recordStore?.recordGet(recordName);
                    if (record) {
                        observables.set(token, record.get$(fieldName, { distinct: true }).pipe(startWith(record.data[fieldName])));
                    }
                }
            }
            if (observables.size) {
                this.captionKeyTokenSubscriptions = this.captionKeyTokenSubscriptions || new Map();
                if (this.captionKeyTokenSubscriptions.has(property)) {
                    this.captionKeyTokenSubscriptions.get(property)?.unsubscribe();
                }
                const sub = combineLatest(Array.from(observables.values())).pipe(observables.size >= 2 ? debounceTime(0) : tap(), takeUntil(this.destroy$))
                    .subscribe(() => {
                    this.parseCaption(property, maybeTemplate);
                    // this.propertySet(
                    // 	property
                    // 	,replaceTokens(maybeTemplate, {
                    // 		...this.record?.data || {}
                    // 		,...this.captionKeyTokens
                    // 	}, {
                    // 		records: this.recordStore?.recordsGet()
                    // 	})
                    // );
                });
                this.captionKeyTokenSubscriptions.set(property, sub);
            }
            else if (this.captionKeyTokenSubscriptions && this.captionKeyTokenSubscriptions.has(property)) {
                this.captionKeyTokenSubscriptions.get(property)?.unsubscribe();
            }
        }
    }
    /**
     * Generates the HTML template for the component.
     * For components where you want to store a reference to some element (ie. Input.inputElement)
     * you can build your DOM manually within the template function, and return a blank string.
     * @returns The HTML template as a string.
     */
    template() {
        return `<span>${this.id} does not have a template defined.</span>`;
    }
    /**
     * Attaches a handler to the event.
     * @param eventName - The name of the event.
     * @param handler - The function to handle the event.
     */
    on(eventName, handler) {
        this.eventHandlers[eventName] = handler;
    }
    /**
     * Attaches a handler that is executed before the event.
     * Return false from your handler to block the event from occurring.
     * @param eventName - The name of the event.
     * @param handler - The function to handle the event.
     */
    before(eventName, handler) {
        this.beforeHandlers[eventName] = handler;
    }
    /**
     * Triggers the event with the given name.
     * @param eventName - The name of the event.
     * @param options
     * @param args - The arguments for the event.
     */
    trigger(eventName, options, ...args) {
        if (this.destroyed) {
            return; // dont think we have any events we'd still want to fire once destroyed. If we do, we can add a flag or something.
        }
        const beforeHandler = this.beforeHandlers[eventName];
        let async = true;
        if (options && Reflect.has(options, 'async')) {
            async = false;
        }
        let beforeReturnValue = beforeHandler ? beforeHandler(...args) : true;
        const postProcessing = (eventReturnValue) => {
            const afterHandler = this.afterHandlers[eventName];
            if (afterHandler) {
                afterHandler(eventReturnValue, ...args); // Invoke the after handler
            }
            // this is for internal usage by the component when we need to do something with the return value from the potentially overriden default handler. 
            if (options?.postEventHandler) {
                options.postEventHandler(eventReturnValue, ...args); // Invoke the internal post event handler
            }
        };
        const processEvent = (beforeReturnValue) => {
            if (!beforeReturnValue) {
                if (options?.eventBlockedHandler) {
                    options.eventBlockedHandler();
                }
                throw new Error('Silent: Before handler blocked event');
            }
            const handler = this.eventHandlers[eventName];
            let defaultEventHandler = handler;
            if (!defaultEventHandler && options?.defaultEventHandler) {
                defaultEventHandler = options?.defaultEventHandler;
            }
            let eventConfig = this.dynamicHandlers[eventName];
            if (!eventConfig || !eventConfig.length) {
                eventConfig = options?.eventConfig;
            }
            const eventConfigRecord = options?.eventConfigRecord ? options.eventConfigRecord : this.record;
            if (eventConfig && eventConfig.length) {
                const eventManager = DIContainer.getInstance('EventConfigManager');
                return eventManager.parse(eventConfig, eventContextGet({
                    initiator: this,
                    record: eventConfigRecord,
                    eventParams: args,
                    defaultEventHandler: defaultEventHandler,
                    event: options?.event,
                    disableMultiRecord: options?.eventConfigDisableMultiRecord
                }));
            }
            else {
                let returnValue;
                if (handler) {
                    const superHandler = options?.defaultEventHandler || (() => { });
                    returnValue = handler(superHandler, ...args); // Invoke the event handler
                }
                else if (options?.defaultEventHandler) {
                    returnValue = options.defaultEventHandler(...args); // Invoke the default event handler
                }
                return returnValue;
            }
        };
        if (!async) {
            if (beforeReturnValue instanceof Observable) {
                console.warn(`BeforeHandler for a synchronous event returned an observable, boolean expected. Component: ${this.id}, Event Name: ${eventName}`);
                beforeReturnValue = true;
            }
            const returnValue = processEvent(beforeReturnValue);
            postProcessing(returnValue);
            return returnValue;
        }
        let obs;
        if (beforeHandler) {
            if (beforeReturnValue instanceof Observable) {
                obs = beforeReturnValue;
            }
            else {
                obs = of(beforeReturnValue);
            }
        }
        else {
            obs = of(true);
        }
        obs = obs.pipe(take(1), catchError((err) => {
            console.error(err);
            return of(false);
        }), switchMap((proceed) => {
            const returnValue = processEvent(proceed);
            if (returnValue instanceof Observable) {
                return returnValue;
            }
            else {
                return of(returnValue);
            }
        }), tap((returnValue) => {
            postProcessing(returnValue);
        }), shareReplay(1));
        obs.subscribe({
            error: err => {
                if (err.message.indexOf('Silent:') === 0) {
                    console.warn(err.message);
                    return;
                }
                console.error(err);
                if (!err.displayed) {
                    eventContextGet({
                        initiator: this
                    }).alert({ caption: err.message });
                }
            }
        });
        return obs;
    }
    /**
     * Attaches a handler that is executed after the event.
     * @param eventName - The name of the event.
     * @param handler - The function to handle the event.
     */
    after(eventName, handler) {
        this.afterHandlers[eventName] = handler;
    }
    /**
     * Observable that emits a boolean indicating whether the provided
     * element is visible or not. True for visible, False for not visible.
     *
     * @param element The HTML element to observe.
     * @param waitForDraw
     * @returns Observable<boolean>
     */
    isVisible$(element, waitForDraw) {
        if (!element) {
            element = this.rootElement;
        }
        let obs;
        // let obs = of(element);
        if (!element || waitForDraw) {
            // component root element might not exist yet, wait for it to be set. 
            obs = this.propertyGet$('drawn')
                .pipe(filter(b => b), take(1), takeUntil(this.destroy$), map(() => {
                return this.rootElement;
            }));
        }
        else {
            obs = of(element);
        }
        return obs.pipe(takeUntil(this.destroy$), switchMap((element) => {
            return new Observable(observer => {
                // Create a new IntersectionObserver
                const intersectionObserver = new IntersectionObserver((entries) => {
                    for (const entry of entries) {
                        // Check if the element is intersecting (visible)
                        observer.next(entry.isIntersecting);
                    }
                });
                // Start observing the provided element
                intersectionObserver.observe(element);
                // Cleanup the observer when the Observable is unsubscribed
                return () => intersectionObserver.disconnect();
            });
        }));
    }
    isVisible(element) {
        if (!element) {
            element = this.rootElement;
        }
        if (!element)
            return false;
        const rect = element.getBoundingClientRect();
        const viewHeight = window.innerHeight || document.documentElement.clientHeight;
        const viewWidth = window.innerWidth || document.documentElement.clientWidth;
        // Check if the element is at least partially visible
        return (rect.bottom > 0 &&
            rect.right > 0 &&
            rect.top < viewHeight &&
            rect.left < viewWidth);
    }
    linkRecord(record) {
        if (this.record) {
            this.unlinkRecord(true);
        }
        this.setupRecordStateSubscription(record);
        this.record = record;
    }
    setupRecordStateSubscription(record) {
        if (record) {
            this.recordStateSubscription = record.propertyGet$('state')
                .subscribe((state) => {
                this.recordState = state;
            });
        }
    }
    removeRecordStateSubscription(keepState = false) {
        if (this.recordStateSubscription) {
            this.recordStateSubscription.unsubscribe();
            this.recordStateSubscription = undefined;
            if (!keepState) {
                this.recordState = 'none';
            }
        }
    }
    /**
     * Unlink the input from its record
     */
    unlinkRecord(keepState = false) {
        if (this.recordSubscription) {
            this.recordSubscription.unsubscribe();
            this.recordSubscription = undefined;
        }
        this.removeRecordStateSubscription(keepState);
        if (!keepState) {
            this.record = undefined;
        }
    }
    handleProgress(percent) {
        this._propertySet('progress', percent);
        if (!this.noThrobber) {
            if (percent < 100) {
                this.createLoadingThrobber();
                this.loadingThrobber.handleProgress(percent);
            }
            else {
                this.destroyLoadingThrobber();
            }
        }
    }
    createLoadingThrobber() {
        if (!this.loadingThrobber) {
            // only create an instance of throbber after the first time the component needs to show progress
            this.loadingThrobber = new LoadingThrobber(getID(this.id), document.createElement('div'));
            this.loadingThrobber.draw();
            this.appendLoadingThrobber();
        }
    }
    appendLoadingThrobber() {
        if (this.loadingThrobberRootElement && this.loadingThrobber) {
            this.loadingThrobberRootElement.style.position = 'relative'; // throbber position is absolute so its parent must be relative to position it correctly to cover the component that is loading.
            this.loadingThrobberRootElement.appendChild(this.loadingThrobber.rootElement);
        }
    }
    destroyLoadingThrobber() {
        if (this.loadingThrobber) {
            // this.loadingThrobber.rootElement.remove();
            this.loadingThrobber.destroy();
            this.loadingThrobber = undefined;
        }
    }
    /**
     * Adds an event listener to the provided element, and tracks the event to clean it up later during destroy
     * returns a unique eventId that can be used by component/external code to disable/enable/unregister the event outside the normal cycle (where we just remove on destroy).
     * @param eventName
     * @param element
     * @param handler
     */
    registerEvent(eventName, element, handler, opts) {
        const eventId = getID('event');
        this.registeredEvents.set(eventId, {
            eventName,
            element,
            handler,
            opts
        });
        this.enableRegisteredEvent(eventId);
        return eventId;
    }
    /**
     * removes the registered event listener from the element and remove it from registered events as it is no longer needed
     * @param eventId
     */
    unRegisterEvent(eventId) {
        this.disableRegisteredEvent(eventId);
        this.registeredEvents.delete(eventId);
    }
    /**
     * Activates a registered event.
     * Example Usage. You might enable/disable a keypress handler when an element is focused.
     * @param eventId
     */
    enableRegisteredEvent(eventId) {
        const event = this.registeredEvents.get(eventId);
        if (event) {
            event.element.addEventListener(event.eventName, event.handler, event.opts);
        }
    }
    /**
     * Deactivates a registered event, but retains it in the registered events Map for later use.
     * @param eventId
     */
    disableRegisteredEvent(eventId) {
        const event = this.registeredEvents.get(eventId);
        if (event) {
            event.element.removeEventListener(event.eventName, event.handler, event.opts);
        }
    }
    /**
     * Programmatically focus a component.
     */
    focus() {
        this.rootElement?.focus();
    }
    /**
     * Programatically defocus a component.
     */
    blur() {
        this.rootElement?.blur();
    }
    handleFocus(event) {
        this.trigger('focus', {
            defaultEventHandler: (_event) => {
                console.log(`${this.id} focused.`);
                this.propertySet('focused', true);
            },
            eventBlockedHandler: () => {
                event.stopPropagation();
                event.preventDefault();
                event.target?.blur();
            }
        }, event);
    }
    handleBlur(event) {
        this.trigger('blur', {
            defaultEventHandler: () => {
                this.propertySet('focused', false);
            },
            eventBlockedHandler: () => {
                event.preventDefault();
                event.stopPropagation();
                event.target?.focus();
            }
        }, event);
    }
    /**
     * Handles key press events.
     *
     * @param {KeyboardEvent} event - The key press event.
     * @return {boolean} - Returns true if the key press event is handled, otherwise returns false.
     */
    handleKeyPress(event) {
        let returnValue = false;
        this.trigger('keypress', {
            async: false,
            defaultEventHandler: this.keypressDefaultEvent.bind(this),
            eventBlockedHandler: () => {
                // console.log('a before handler blocked default keypress handling: ' + event.key);
                event.preventDefault();
                event.stopPropagation();
                returnValue = true;
            }
        }, event);
        return returnValue;
    }
    /**
     * defaultEventHandler for the keypress event.
     * @param event
     * @protected
     */
    keypressDefaultEvent(event) {
        // if hotkey is handled then stop event propagation and default behavior.
        if (this.handleHotKey(event)) {
            event.preventDefault();
            event.stopPropagation();
            // console.log('hot key handled', event.key)
            return true;
        }
        // console.log(`${this.id} validateKeyPress: ${event.key}`);
        // if hotkey was not handled, validate key press (applicable to stuff like inputNumber validating against alpha characters).
        if (!this.validateKeyPress(event)) {
            // console.log(`${this.id} validated a keypress event and blocked the  key: ${event.key}`)
            event.preventDefault();
            event.stopPropagation();
            return true;
        }
        return false;
    }
    /**
     * Handles a hotkey event.
     *
     * @private
     * @param {KeyboardEvent} event - The keyboard event.
     * @return {boolean} - Returns true if a hotkey was handled and the default keypress was blocked, false otherwise.
     */
    handleHotKey(event) {
        const ctrl = isCtrlKey(event);
        const alt = event.altKey;
        const shift = event.shiftKey;
        const key = `${ctrl ? 'ctrl+' : ''}${alt ? 'alt+' : ''}${shift ? 'shift+' : ''}${event.key ? event.key.toLowerCase() : ''}`;
        if (key === 'ctrl+control' || key === 'alt+alt')
            return;
        // console.log(`${this.id} handleHotKey: ${event.key}`);
        // Check for hotkeys 
        let hotkeyAction = this.hotkeys[key];
        if (!hotkeyAction) {
            hotkeyAction = this.hotkeys['*'];
        }
        if (hotkeyAction && hotkeyAction(event, key)) {
            console.log(`${this.id} handled a hotkey event and blocked the default keypress: ${key}`);
            return true;
        }
        if ((ctrl || alt || key === 'enter' || key === 'escape') && this.rootElement && this.rootElement.parentElement) {
            // call parent element to see if it has a hotkey. Only if a ctrl or alt key is held. (or if they pressed enter)
            const component = DIContainer.getInstance('ComponentRegistry').getComponentByElement(this.rootElement.parentElement, true);
            if (component && component.handleHotKey(event)) {
                return true;
            }
        }
        // return false if the key was not handled
        return false;
    }
    /**
     * Validate key value when pressed (for things like inputs)
     * return false explicitly to block the keypress default behavior.
     * return true to allow the keypress default behavior.
     * @param event
     */
    validateKeyPress(_event) {
        return true;
    }
    renderHeight() {
        if (this.rootElement) {
            const height = this._height;
            // Check if height needs px appended to it
            if (!isNaN(Number(height))) {
                this.rootElement.style.height = `${height}px`;
            }
            else {
                this.rootElement.style.height = height || '';
            }
        }
    }
    renderWidth() {
        if (this.rootElement) {
            const width = this._width;
            // Check if width needs px appended to it
            if (!isNaN(Number(width))) {
                this.rootElement.style.width = `${width}px`;
            }
            else {
                this.rootElement.style.width = width || '';
            }
        }
    }
    renderFlexGrow() {
        if (this.rootElement) {
            const flexGrow = typeof this._flexGrow === 'undefined' ? '' : this._flexGrow.toString();
            this.rootElement.style.flexGrow = flexGrow;
        }
    }
    renderFlexShrink() {
        if (this.rootElement) {
            const flexShrink = typeof this._flexShrink === 'undefined' ? '' : this._flexShrink.toString();
            this.rootElement.style.flexShrink = flexShrink;
        }
    }
    renderFlexBasis() {
        if (this.rootElement) {
            let flexBasis = typeof this._flexBasis === 'undefined' ? '' : this._flexBasis.toString();
            if (flexBasis !== '' && !isNaN(Number(flexBasis))) {
                flexBasis = `${flexBasis}px`;
            }
            this.rootElement.style.flexBasis = flexBasis;
        }
    }
    omitDesignerProperty(prop) {
        this.omittedDesignerProperties = this.omittedDesignerProperties || new Set();
        this.omittedDesignerProperties.add(prop);
    }
    formDesignerPropertiesGet(properties) {
        const parent = this.parentComponentGet();
        properties.id = ({
            id: 'id',
            value: this.id,
            componentType: 'Input',
            componentProperties: {
                disabled: !!this.compositeChild
            },
            captionKey: 'formbuilder_id',
            propertyGroup: '',
            propertyIndex: 0,
            saveFlag: false
        });
        properties.componentName = ({
            id: 'componentName',
            value: this.componentName,
            componentType: 'Input',
            componentProperties: {
                disabled: true,
                labelWidth: 'auto'
            },
            captionKey: 'formbuilder_componenttype',
            propertyGroup: '',
            propertyIndex: 0.1,
            saveFlag: false
        });
        properties.seq = ({
            id: 'seq',
            value: this._seq,
            componentType: 'InputNumber',
            componentProperties: {
                disabled: true
            },
            captionKey: 'formbuilder_seq',
            propertyGroup: 'layout',
            propertyIndex: 0,
            saveFlag: false
        });
        if (this._autoResize) {
            properties.fixedHeight = ({
                id: 'fixedHeight',
                value: this._fixedHeight,
                componentType: 'Input',
                componentProperties: {},
                captionKey: 'formbuilder_fixedheight',
                propertyGroup: 'layout',
                propertyIndex: 99,
                saveFlag: !!this._fixedHeight
            });
        }
        properties.dependentCaptionKeys = {
            id: 'dependentCaptionKeys',
            value: [],
            componentType: 'Input',
            captionKey: '',
            componentProperties: {
                hidden: true
            },
            propertyGroup: 'caption',
            saveFlag: false
        };
        if (this.captionTypes && Object.keys(this.captionTypes).length) {
            properties.captionKey = ({
                id: 'captionKey',
                value: this._captionKey,
                componentType: 'Input',
                componentProperties: {
                // labelPosition: 'top' // TODO: we cant add this until we fix the styling for button container to go beside the input instead of below it.
                },
                captionKey: 'formbuilder_captionkey',
                propertyGroup: 'caption',
                propertyIndex: 0,
                saveFlag: false
            });
            forEachKey(this.captionTypes, (property, captionType) => {
                properties[property] = ({
                    id: property,
                    value: this.CaptionManager.captions[`${this.captionKey}_${captionType}`],
                    componentType: 'Input',
                    componentProperties: {
                        disabled: true
                    },
                    captionKey: `q360_q360caption_${property.toLowerCase()}`,
                    propertyGroup: 'caption',
                    propertyIndex: 1,
                    refreshPanelOnChange: true,
                    saveFlag: false
                });
            });
        }
        properties.viewCondition = {
            id: 'viewCondition',
            value: this.viewCondition,
            componentType: 'ConditionsEditor',
            componentProperties: {
                labelPosition: 'top',
                addData: true,
                addBoolean: true
            },
            captionKey: 'formbuilder_viewcondition',
            propertyGroup: 'permissions',
            propertyIndex: 3,
            handlePropertyChange: (newValue, _oldValue, source) => {
                this.viewCondition = newValue;
                this.hidden = !DIContainer.getInstance('ConditionsManager').validateConditions(newValue, {
                    initiator: this,
                    record: this.record
                });
                return newValue;
            },
            saveFlag: false
        };
        if (this.compositeChild) {
            delete properties.viewCondition;
        }
        properties.enabledCondition = {
            id: 'enabledCondition',
            value: this.enabledCondition,
            componentType: 'ConditionsEditor',
            componentProperties: {
                labelPosition: 'top',
                addData: true,
                addBoolean: true
            },
            captionKey: 'formbuilder_enabledcondition',
            propertyGroup: 'permissions',
            propertyIndex: 4,
            handlePropertyChange: (newValue, _oldValue, source) => {
                this.enabledCondition = newValue;
                this.disabled = !DIContainer.getInstance('ConditionsManager').validateConditions(newValue, {
                    initiator: this,
                    record: this.record
                });
                return newValue;
            },
            saveFlag: !!this.enabledCondition
        };
        if (this.globalId === this.formContext?.globalId) {
            delete properties.enabledCondition;
        }
        properties.comments = {
            id: 'comments',
            value: this.comments,
            componentType: 'InputText',
            componentProperties: {
                labelPosition: 'top',
                lines: 4
            },
            captionKey: 'formbuilder_comments',
            propertyGroup: 'comments',
            propertyIndex: 4,
            saveFlag: false
        };
        if (this.globalId === this.formContext?.globalId) {
            delete properties.comments;
        }
        properties.enabledRecordStates = ({
            id: 'enabledRecordStates',
            value: this._enabledRecordStates,
            componentType: 'Select',
            componentProperties: {
                multiSelect: true,
                multiSelectValueType: 'ARRAY',
                staticOptionData: [
                    { value: 'add', display: 'Add' },
                    { value: 'edit', display: 'Edit' },
                    { value: 'none', display: 'No State' },
                    { value: 'read', display: 'Read' }
                ]
            },
            captionKey: 'formbuilder_enabledstates',
            propertyGroup: 'permissions',
            propertyIndex: 1,
            saveFlag: () => !!(this._enabledRecordStates && this._enabledRecordStates.length)
        });
        properties.visibleRecordStates = ({
            id: 'visibleRecordStates',
            value: this._visibleRecordStates,
            componentType: 'Select',
            componentProperties: {
                multiSelect: true,
                multiSelectValueType: 'ARRAY',
                staticOptionData: [
                    { value: 'add', display: 'Add' },
                    { value: 'edit', display: 'Edit' },
                    { value: 'none', display: 'No State' },
                    { value: 'read', display: 'Read' }
                ]
            },
            captionKey: 'formbuilder_visiblestates',
            propertyGroup: 'permissions',
            propertyIndex: 2,
            saveFlag: () => !!(this._visibleRecordStates && this._visibleRecordStates.length)
        });
        /*properties.visibleViews = ({
            id: 'visibleViews'
            ,value: this._visibleViews
            ,componentType: 'Select'
            ,componentProperties: {
                multiSelect: true
                ,multiSelectValueType: 'ARRAY'
                ,staticOptionData: [
                    {value: 'full', display: 'Full'}
                    ,{value: 'tech', display: 'Tech'}
                ]
            }
            ,captionKey: 'formbuilder_visibleviews'
            ,propertyGroup: 'permissions'
            ,propertyIndex: 2.1
            ,saveFlag: !!(this._visibleViews && this._visibleViews.length)
        })*/
        if (parent && parent.componentName === 'FlexContainer') {
            properties.flexBasis = ({
                id: 'flexBasis',
                value: this._flexBasis,
                componentType: 'Input',
                componentProperties: {},
                captionKey: 'formbuilder_flexbasis',
                propertyGroup: 'layout',
                propertyIndex: 1,
                saveFlag: () => !!this._flexBasis
            });
            properties.flexGrow = ({
                id: 'flexGrow',
                value: typeof this._flexGrow == 'undefined' ? 1 : this._flexGrow,
                componentType: 'InputRange',
                componentProperties: {
                    min: 0,
                    max: 10,
                    dataList: [
                        { value: '0', caption: '0' },
                        { value: '5', caption: '5' },
                        { value: '10', caption: '10' }
                    ]
                },
                captionKey: 'formbuilder_flexgrow',
                propertyGroup: 'layout',
                propertyIndex: 1,
                saveFlag: () => !isNaN(Number(this._flexGrow))
            });
            properties.flexShrink = ({
                id: 'flexShrink',
                value: typeof this._flexShrink == 'undefined' ? 1 : this._flexShrink,
                componentType: 'InputRange',
                componentProperties: {
                    min: 0,
                    max: 10,
                    dataList: [
                        { value: '0', caption: '0' },
                        { value: '5', caption: '5' },
                        { value: '10', caption: '10' }
                    ]
                },
                captionKey: 'formbuilder_flexshrink',
                propertyGroup: 'layout',
                propertyIndex: 1,
                saveFlag: () => !isNaN(Number(this._flexShrink))
            });
        }
        if (this.dynamicCodeEvents) {
            forEachKey(this.dynamicCodeEvents, (eventCategory, eventName) => {
                if (!eventCategory) {
                    return;
                }
                let captionKey = `formbuilder_event_${eventName}`;
                const componentProperties = {
                    labelPosition: 'top',
                    eventCategory
                };
                if (!this.CaptionManager.hasKey(`formbuilder_event_${eventName}`)) {
                    // could be a dynamic event from a user added button, the name will not be defined anywhere.
                    captionKey = '';
                    componentProperties.labelCaption = this.CaptionManager.getCaption('formbuilder_event_unknown_caption', {
                        eventname: eventName
                    });
                }
                properties[`dynamicHandlers.${eventName}`] = ({
                    id: `dynamicHandlers.${eventName}`,
                    value: this.dynamicHandlers[eventName],
                    componentType: 'EventConfigEditor',
                    componentProperties: componentProperties,
                    captionKey: captionKey,
                    propertyGroup: 'events',
                    propertyIndex: 0,
                    saveFlag: false
                });
                properties.dynamicHandlers = ({
                    id: 'dynamicHandlers',
                    value: this.dynamicHandlers,
                    componentType: '',
                    componentProperties: {
                        hidden: true
                    },
                    captionKey: '',
                    propertyGroup: 'events',
                    propertyIndex: 0,
                    saveFlag: () => {
                        let found = false;
                        if (Object.keys(this.dynamicHandlers).length) {
                            forEachKey(this.dynamicHandlers, (handlers) => {
                                if (handlers.length) {
                                    found = true;
                                }
                            });
                        }
                        return found;
                    }
                });
            });
        }
        else if (this.dynamicHandlers) {
            properties.dynamicHandlers = ({
                id: 'dynamicHandlers',
                value: this.dynamicHandlers,
                componentType: '',
                componentProperties: {
                    hidden: true
                },
                captionKey: '',
                propertyGroup: 'events',
                propertyIndex: 0,
                saveFlag: () => {
                    let found = false;
                    if (Object.keys(this.dynamicHandlers).length) {
                        forEachKey(this.dynamicHandlers, (handlers) => {
                            if (handlers.length) {
                                found = true;
                            }
                        });
                    }
                    return found;
                }
            });
        }
        return properties;
    }
    parentComponentGet() {
        if (this.formContext && this.formContext.componentGet(this.containerId)) {
            return this.formContext.componentGet(this.containerId);
        }
        if (this.globalContainerId) {
            return DIContainer.getInstance('ComponentRegistry').getComponent(this.globalContainerId);
        }
        if (!this.rootElement?.parentElement) {
            return;
        }
        return DIContainer.getInstance('ComponentRegistry').getComponentByElement(this.rootElement.parentElement);
    }
    /**
     * Called when user switches between mobile and full view so components with mobile specific behavior can react
     * @param mobile
     * @protected
     */
    handleMobileSwitch(_mobile) {
    }
    handleSizingSwitch(_size) {
        if (this.autoResize) {
            this.formContext?.debouncedHandleResize();
        }
    }
    handleThemeSwitch(theme) {
    }
    handleTimezoneSwitch(_zone) {
    }
    handleDatetimeFormatSwitch(_format) {
    }
    handleFullscreenSwitch(_fullscreenElement) {
    }
    /*handleViewSwitch(_view: 'tech'|'full') {
        if (this._visibleViews && this._visibleViews.length) {
            this.updateHiddenState();
        }
    }*/
    registerTooltip(element, options = {}) {
        const callbackId = DIContainer.getInstance('TooltipManager').registerTooltip(element, options);
        if (callbackId) {
            this.toolTipCallbackIds.set(callbackId, true);
        }
        return callbackId;
    }
    unregisterTooltip(element, callbackId) {
        callbackId = callbackId || element?.dataset.tooltipCallbackId;
        if (callbackId) {
            this.toolTipCallbackIds.delete(callbackId);
            DIContainer.getInstance('TooltipManager').unregisterTooltip(element, callbackId);
        }
    }
    destroy() {
        super.destroy();
        this.eventHandlers = {};
        this.afterHandlers = {};
        this.beforeHandlers = {};
        this.dependencies = {};
        delete this.recordStore;
        delete this.formContext;
        delete this.loadingThrobberRoot;
        this.unlinkRecord();
        this.destroyLoadingThrobber();
        delete this.defaultPropertiesObject;
        DIContainer.getInstance('ComponentRegistry').unregisterComponent(this.globalId);
        this.captionKeyTokenSubscriptions?.clear();
        if (this.rootElement && this.rootElement.parentNode) {
            this.rootElement.remove();
        }
        this.toolTipCallbackIds.forEach((o, callbackId) => {
            DIContainer.getInstance('TooltipManager').unregisterTooltip(undefined, callbackId);
        });
        this.registeredEvents.forEach((o) => {
            o.element.removeEventListener(o.eventName, o.handler);
        });
        this.registeredEvents.clear();
        this.drawn = false;
        // null out properties for clarity and to aid garbage collection
        this.rootElement = null;
    }
}
/** The component version. */
Component.VERSION = '$Revision: 130780 $';
